import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

interface SubCategory {
  label: string;
  href: string;
}

interface MainCategory {
  label: string;
  href: string;
  subcategories?: SubCategory[];
}

const collections: MainCategory[] = [
  {
    label: "Bunker Walls",
    href: "/collections/bunker-walls",
    subcategories: [
      { label: "4ft Bunker Wall", href: "/product/4ft-bunker-wall" },
      { label: "6ft Bunker Wall", href: "/product/6ft-bunker-wall" },
      { label: "8ft Bunker Wall", href: "/product/8ft-bunker-wall" },
      { label: "10ft Bunker Wall", href: "/product/10ft-bunker-wall" },
    ],
  },
  {
    label: "Safety",
    href: "/collections/safety",
    subcategories: [
      { label: "Safety Doors", href: "/collections/safety-doors" },
      { label: "Safety Gates", href: "/collections/safety-gates" },
    ],
  },
  {
    label: "Aeration",
    href: "/collections/aeration",
    subcategories: [
      { label: "Aeration Mesh", href: "/collections/aeration-mesh" },
      { label: "Aeration Sheets", href: "/collections/aeration-sheets" },
      { label: "Pipe & Fittings", href: "/collections/pipe-fittings" },
      { label: "Fans & Transitions", href: "/collections/fans-transitions" },
    ],
  },
  {
    label: "Parts",
    href: "/collections/parts",
    subcategories: [
      { label: "Aeration Sheets", href: "/collections/replacement-aeration-sheets" },
      { label: "Top Plate Flashing", href: "/collections/top-plate-flashing" },
      { label: "Wall Flashing", href: "/collections/wall-flashing" },
      { label: "Ground Stakes", href: "/collections/ground-stakes" },
      { label: "Grating", href: "/collections/grating" },
    ],
  },
];

interface ProductsMegaMenuProps {
  isMobile?: boolean;
  onItemClick?: () => void;
}

const ProductsMegaMenu = ({ isMobile = false, onItemClick }: ProductsMegaMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const handleItemClick = () => {
    setIsOpen(false);
    setExpandedCategory(null);
    onItemClick?.();
  };

  if (isMobile) {
    return (
      <div className="w-full">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between w-full text-sm font-medium tracking-widest text-foreground hover:text-primary transition-colors py-2"
        >
          PRODUCTS
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        
        {isOpen && (
          <div className="pl-4 mt-2 space-y-2 border-l border-border/50">
            {collections.map((category) => (
              <div key={category.label}>
                {category.subcategories ? (
                  <>
                    <button
                      onClick={() => setExpandedCategory(
                        expandedCategory === category.label ? null : category.label
                      )}
                      className="flex items-center justify-between w-full text-sm text-foreground/80 hover:text-primary transition-colors py-1.5"
                    >
                      {category.label}
                      <ChevronRight className={`w-3 h-3 transition-transform ${
                        expandedCategory === category.label ? "rotate-90" : ""
                      }`} />
                    </button>
                    {expandedCategory === category.label && (
                      <div className="pl-4 mt-1 space-y-1">
                        <Link
                          to={category.href}
                          onClick={handleItemClick}
                          className="block text-xs text-muted-foreground hover:text-primary transition-colors py-1"
                        >
                          All {category.label}
                        </Link>
                        {category.subcategories.map((sub) => (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            onClick={handleItemClick}
                            className="block text-xs text-muted-foreground hover:text-primary transition-colors py-1"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={category.href}
                    onClick={handleItemClick}
                    className="block text-sm text-foreground/80 hover:text-primary transition-colors py-1.5"
                  >
                    {category.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop mega menu
  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => {
        setIsOpen(false);
        setExpandedCategory(null);
      }}
    >
      <button
        className="flex items-center gap-1 text-sm font-medium tracking-widest text-white/90 hover:text-primary transition-colors"
      >
        PRODUCTS
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
          <div className="bg-background/95 backdrop-blur-md border border-border/50 rounded-lg shadow-xl min-w-[720px] p-8">
            <div className="grid grid-cols-4 gap-8">
              {collections.map((category) => (
                <div key={category.label} className="space-y-4">
                  <Link
                    to={category.href}
                    onClick={handleItemClick}
                    className="block text-base font-semibold text-primary hover:text-primary/80 transition-colors"
                  >
                    {category.label}
                  </Link>
                  
                  {category.subcategories && (
                    <div className="space-y-2.5">
                      {category.subcategories.map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.href}
                          onClick={handleItemClick}
                          className="block text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-8 pt-5 border-t border-border/50">
              <Link
                to="/collections/all"
                onClick={handleItemClick}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                View All Products →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsMegaMenu;
